import { useState } from "react";
import { useTranslation } from "../i18n/LanguageContext";

// Shared message composer for Buddies.js's embedded chat panel and
// ChatPage.js. A <textarea> (not <input>) so Shift+Enter can insert a real
// newline; Enter alone submits on desktop. Disabled while a send is in
// flight so a double Enter/double-tap can't fire two sends.
function MessageComposer({ onSend, sending, disabled }) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");

  const canSend = value.trim().length > 0 && !sending && !disabled;

  const submit = async () => {
    if (!canSend) return;
    const text = value;
    setValue("");
    const ok = await onSend(text);
    // Restore the draft if sending genuinely failed, so the user doesn't
    // lose what they typed.
    if (!ok) setValue(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
    // Shift+Enter: let the default newline behavior happen.
  };

  return (
    <div className="flex items-end gap-2 sm:gap-3">
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        rows={1}
        placeholder={t("buddies.messagePlaceholder", "Write a message...")}
        title={t("chat.composerHint", "Enter to send, Shift+Enter for a new line")}
        disabled={disabled}
        className="max-h-32 min-h-[2.75rem] flex-grow resize-none overflow-y-auto rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-sm text-white placeholder-gray-400 transition focus:border-transparent focus:ring-2 focus:ring-blue-400 disabled:opacity-60"
      />
      <button
        onClick={submit}
        disabled={!canSend}
        title={t("chat.sendTitle", "Send message")}
        className="shrink-0 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 font-semibold text-white shadow-lg transition-all duration-200 hover:from-blue-500 hover:to-indigo-500 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:from-blue-600 disabled:hover:to-indigo-600"
      >
        {sending ? "…" : "➤"}
      </button>
    </div>
  );
}

export default MessageComposer;
